import { useMemo } from 'react'
import useSWR from 'swr'

import { useLocaleStore } from '@/store/locale'
import {
  getDependencyParam,
  getRemoteEndpoint,
  RemoteSelectPrependOption
} from '@/utils/form/remote-select'

import { TranslatedField } from '@/api/types'

export type RemoteOption = {
  id: number
  name: TranslatedField | string
  [key: string]: any
}

export type RemoteOptionsResponse = {
  data: RemoteOption[]
}

type UseRemoteSelectOptionsParams = {
  remoteKey: string | null
  dependsOn: string | null
  dependencyValue: string | number | null
  prependOption?: RemoteSelectPrependOption | null
  enabled?: boolean
}

/**
 * Hook to fetch remote select options
 * Handles dependency-based fetching (e.g., cities by country)
 */
export const useRemoteSelectOptions = ({
  remoteKey,
  dependsOn,
  dependencyValue,
  prependOption,
  enabled = true
}: UseRemoteSelectOptionsParams) => {
  const currentLocale = useLocaleStore((state) => state.currentLocale) || 'it'

  // Build the API URL
  const url = useMemo(() => {
    if (!remoteKey || !enabled) return null

    const endpoint = getRemoteEndpoint(remoteKey)
    if (!endpoint) return null

    // If field has a dependency but no value selected, don't fetch
    if (dependsOn && !dependencyValue) return null

    // Build URL with query params if there's a dependency
    if (dependsOn && dependencyValue) {
      const paramName = getDependencyParam(dependsOn)
      return `${endpoint}?${paramName}=${dependencyValue}`
    }

    return endpoint
  }, [remoteKey, dependsOn, dependencyValue, enabled])

  const { data, error, isLoading, mutate } = useSWR<RemoteOptionsResponse>(url)

  // Transform data to choices format
  const choices = useMemo(() => {
    const mappedChoices =
      data?.data?.map((item) => {
        const text =
          typeof item.name === 'string'
            ? item.name
            : item.name[currentLocale as keyof TranslatedField] || item.name.it || item.name.en

        return {
          value: String(item.id),
          text: text || String(item.id)
        }
      }) || []

    if (!prependOption) return mappedChoices

    return [
      { value: prependOption.value ?? '__NULL__', text: prependOption.label },
      ...mappedChoices
    ]
  }, [data, currentLocale, prependOption])

  return {
    choices,
    isLoading,
    error,
    mutate,
    hasData: choices.length > 0
  }
}
